import {
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useUser } from "@/contexts/UserContext";
import "./InterfaceBuilderToolbar.css";

const TOOLBAR_VIEWPORT_MARGIN = 8;
const TOOLBAR_KEYBOARD_MOVE_STEP = 16;

interface ToolbarPosition {
  x: number;
  y: number;
}

interface ToolbarDragState {
  offsetX: number;
  offsetY: number;
  panelWidth: number;
  panelHeight: number;
}

export function InterfaceBuilderToolbar() {
  const { t } = useTranslation();
  const {
    isInterfaceBuilderInspectorOpen,
    setInterfaceBuilderMode,
    toggleInterfaceBuilderInspector,
  } = useUser();
  const panelRef = useRef<HTMLDivElement>(null);
  const [toolbarPosition, setToolbarPosition] =
    useState<ToolbarPosition | null>(null);
  const [dragState, setDragState] = useState<ToolbarDragState | null>(null);
  const isFloating = toolbarPosition !== null;

  const constrainToolbarPosition = useCallback((
    x: number,
    y: number,
    panelWidth: number,
    panelHeight: number,
  ): ToolbarPosition => {
    const maxX = Math.max(
      TOOLBAR_VIEWPORT_MARGIN,
      window.innerWidth - panelWidth - TOOLBAR_VIEWPORT_MARGIN,
    );
    const maxY = Math.max(
      TOOLBAR_VIEWPORT_MARGIN,
      window.innerHeight - panelHeight - TOOLBAR_VIEWPORT_MARGIN,
    );

    return {
      x: clamp(x, TOOLBAR_VIEWPORT_MARGIN, maxX),
      y: clamp(y, TOOLBAR_VIEWPORT_MARGIN, maxY),
    };
  }, []);

  const moveToolbarBy = useCallback((deltaX: number, deltaY: number) => {
    const rect = panelRef.current?.getBoundingClientRect();
    if (!rect) return;

    setToolbarPosition((currentPosition) => {
      const sourcePosition = currentPosition ?? {
        x: rect.left,
        y: rect.top,
      };

      return constrainToolbarPosition(
        sourcePosition.x + deltaX,
        sourcePosition.y + deltaY,
        rect.width,
        rect.height,
      );
    });
  }, [constrainToolbarPosition]);

  const resetToolbarPosition = useCallback(() => {
    setToolbarPosition(null);
  }, []);

  const handleDragStart = useCallback((
    event: ReactPointerEvent<HTMLButtonElement>,
  ) => {
    if (event.button !== 0) return;

    const rect = panelRef.current?.getBoundingClientRect();
    if (!rect) return;

    event.currentTarget.setPointerCapture(event.pointerId);
    event.preventDefault();

    setDragState({
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top,
      panelWidth: rect.width,
      panelHeight: rect.height,
    });
    setToolbarPosition(constrainToolbarPosition(
      rect.left,
      rect.top,
      rect.width,
      rect.height,
    ));
  }, [constrainToolbarPosition]);

  const handleDragHandleKeyDown = useCallback((
    event: ReactKeyboardEvent<HTMLButtonElement>,
  ) => {
    if (event.key === "Escape" || event.key === "Home") {
      event.preventDefault();
      resetToolbarPosition();
      return;
    }

    if (event.key === "ArrowLeft") {
      event.preventDefault();
      moveToolbarBy(-TOOLBAR_KEYBOARD_MOVE_STEP, 0);
    } else if (event.key === "ArrowRight") {
      event.preventDefault();
      moveToolbarBy(TOOLBAR_KEYBOARD_MOVE_STEP, 0);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      moveToolbarBy(0, -TOOLBAR_KEYBOARD_MOVE_STEP);
    } else if (event.key === "ArrowDown") {
      event.preventDefault();
      moveToolbarBy(0, TOOLBAR_KEYBOARD_MOVE_STEP);
    }
  }, [moveToolbarBy, resetToolbarPosition]);

  useEffect(() => {
    if (!dragState) return;

    const handlePointerMove = (event: PointerEvent) => {
      setToolbarPosition(constrainToolbarPosition(
        event.clientX - dragState.offsetX,
        event.clientY - dragState.offsetY,
        dragState.panelWidth,
        dragState.panelHeight,
      ));
    };

    const handlePointerUp = () => {
      setDragState(null);
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
    window.addEventListener("pointercancel", handlePointerUp);

    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
      window.removeEventListener("pointercancel", handlePointerUp);
    };
  }, [constrainToolbarPosition, dragState]);

  useEffect(() => {
    if (!toolbarPosition) return;

    const handleResize = () => {
      const rect = panelRef.current?.getBoundingClientRect();
      if (!rect) return;

      setToolbarPosition((currentPosition) => (
        currentPosition
          ? constrainToolbarPosition(
            currentPosition.x,
            currentPosition.y,
            rect.width,
            rect.height,
          )
          : currentPosition
      ));
    };

    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, [constrainToolbarPosition, toolbarPosition]);

  const toolbarStyle: CSSProperties | undefined = toolbarPosition
    ? {
      bottom: "auto",
      justifyContent: "flex-start",
      left: toolbarPosition.x,
      right: "auto",
      top: toolbarPosition.y,
    }
    : undefined;
  const selectLabel = t("interfaceBuilderToolbar.tools.select");
  const inspectorLabel = t("interfaceBuilderToolbar.tools.inspector");
  const exitLabel = t("interfaceBuilderToolbar.tools.exit");

  return (
    <div
      className={[
        "interface-builder-toolbar",
        isFloating ? "interface-builder-toolbar--floating" : "",
        dragState ? "interface-builder-toolbar--dragging" : "",
      ].filter(Boolean).join(" ")}
      style={toolbarStyle}
    >
      <div
        className="interface-builder-toolbar__panel"
        role="toolbar"
        aria-label={t("interfaceBuilderToolbar.label")}
        ref={panelRef}
      >
        <button
          className="interface-builder-toolbar__handle"
          type="button"
          aria-label={t("interfaceBuilderToolbar.dragHandle")}
          title={t("interfaceBuilderToolbar.dragHandle")}
          onKeyDown={handleDragHandleKeyDown}
          onPointerDown={handleDragStart}
        >
          <span
            className="interface-builder-toolbar__handle-grip"
            aria-hidden="true"
          >
            <span />
            <span />
            <span />
            <span />
          </span>
        </button>
        <div className="interface-builder-toolbar__group" role="group">
          <button
            className="interface-builder-toolbar__tool interface-builder-toolbar__tool--active"
            type="button"
            aria-label={selectLabel}
            aria-pressed="true"
            title={selectLabel}
          >
            <SelectIcon />
          </button>
        </div>
        <div className="interface-builder-toolbar__group" role="group">
          <button
            className={[
              "interface-builder-toolbar__tool",
              isInterfaceBuilderInspectorOpen
                ? "interface-builder-toolbar__tool--active"
                : "",
            ].filter(Boolean).join(" ")}
            type="button"
            aria-label={inspectorLabel}
            aria-pressed={isInterfaceBuilderInspectorOpen}
            title={inspectorLabel}
            onClick={toggleInterfaceBuilderInspector}
          >
            <InspectorIcon />
          </button>
        </div>
        <div className="interface-builder-toolbar__group" role="group">
          <button
            className="interface-builder-toolbar__tool interface-builder-toolbar__tool--exit"
            type="button"
            aria-label={exitLabel}
            title={exitLabel}
            onClick={() => setInterfaceBuilderMode(false)}
          >
            <ExitIcon />
          </button>
        </div>
      </div>
    </div>
  );
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function SelectIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      aria-hidden
    >
      <path d="M4 4l7 16 2-7 7-2L4 4z" />
    </svg>
  );
}

function ExitIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      aria-hidden
    >
      <path d="M6 6l12 12" />
      <path d="M18 6L6 18" />
    </svg>
  );
}

function InspectorIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      aria-hidden
    >
      <path d="M4 5h16" />
      <path d="M4 12h10" />
      <path d="M4 19h16" />
      <path d="M18 9v6" />
      <path d="M15 12h6" />
    </svg>
  );
}
